import './script';
import { computeRatioAndColors, hex2rgb, hsv2Rgb, rgb2Hsv, rgb2hex } from './script/color';
import { getQueryParams, setQueryParams } from './script/query-params';

// CSS
import './style/reset.css';
import './style/font.css';
import './style/main.css';

const hexPattern = /^[0-9a-fA-F]*$/;

const defaultFg = 'a5b6ff';
const defaultBg = '141824';
const hsvSettings = [
	{ label: 'Hue', max: '360' },
	{ label: 'Saturation', max: '100' },
	{ label: 'Brightness', max: '100' },
];

const color = {
	foreground: { r: 165, g: 182, b: 255 },
	background: { r: 20, g: 24, b: 36 },
};

const sentence = [
	'優しくも鮮やかに空を染める桜木の下。となりを歩く彼女は不意に僕の髪へ触れるとクスリと笑い「ね、ついてたよ」と指先から淡いピンクの花びらを覗かせた。',
	"Under the cherry blossoms that gently and vividly dye the sky, she walks beside me and suddenly touches my hair. With a playful smile, she says, 'Look, you have something on you,' revealing a delicate pink petal from her fingertips.",
];

window.addEventListener('DOMContentLoaded', () => {
	// Templates
	const colorInputTemplate = document.querySelector<HTMLTemplateElement>('#color-input')?.content;
	const rangeInputTemplate = document.querySelector<HTMLTemplateElement>('#range-input')?.content;

	// Elements
	const inputWrapperElem = document.querySelector<HTMLDivElement>('.input-wrapper');
	const contrastRatioElem = document.querySelector<HTMLSpanElement>('#contrast-result > #ratio');
	const contrastEvaluationElem = document.querySelector<HTMLSpanElement>('#contrast-result > #evaluation');
	const sampleSentenceElem = document.querySelector<HTMLDivElement>('.sample-sentence');

	// Caches
	const colorInputElems: HTMLInputElement[] = [];
	const rangeInputElems: HTMLInputElement[][] = [];

	function pushState(inputId: string, value: string): void {
		if (value.length === 6 && hexPattern.test(value)) {
			setQueryParams(inputId, value);

			window.dispatchEvent(new Event('pushstate'));
		}
	}

	function getContrastRating(contrastRatio: number): string {
		if (contrastRatio >= 8.0) return 'AAA';
		// else if (contrastRatio >= 7.0) return 'AAA Large';
		else if (contrastRatio >= 4.5) return 'AA';
		else if (contrastRatio >= 3.0) return 'AA Large';
		else return 'Fail';
	}

	function setHSV(id: string, value: string) {
		const isForeground = id.includes('foreground');
		const hsvElems = isForeground ? rangeInputElems.map((elem) => elem[0]) : rangeInputElems.map((elem) => elem[1]);

		const [r, g, b] = hex2rgb(value);
		const hsv = rgb2Hsv(r, g, b);

		hsvElems.forEach((elem, i) => {
			elem.value = hsv[i].toString();
		});
	}

	function setColor(foregroundColor?: string, backgroundColor?: string): string[] {
		let fgColor = foregroundColor || getQueryParams('foreground');
		let bgColor = backgroundColor || getQueryParams('background');

		if (!fgColor) {
			fgColor = defaultFg;
			setQueryParams('foreground', fgColor, true);
		}
		if (!bgColor) {
			bgColor = defaultBg;
			setQueryParams('background', bgColor, true);
		}

		const {
			contrastRatio,
			colors: [txtClr, bgClr],
			inputColor,
		} = computeRatioAndColors(fgColor, bgColor);

		// Set color
		document.body.style.setProperty('--txt-clr', `rgb(${txtClr})`);
		document.body.style.setProperty('--bg-clr', `rgb(${bgClr})`);
		document.body.style.setProperty('--input-txt-clr', inputColor);
		document.body.style.setProperty(
			'--saturation-fg-clr',
			`rgb(${hsv2Rgb(rangeInputElems[0][0].value, '100', '100')})`,
		);
		document.body.style.setProperty(
			'--saturation-bg-clr',
			`rgb(${hsv2Rgb(rangeInputElems[0][1].value, '100', '100')})`,
		);

		// Sec result
		if (contrastRatioElem && contrastEvaluationElem) {
			const roundedRatio = Math.round(contrastRatio * 1000) / 1000; // Format [XX.XXX]

			contrastRatioElem.textContent = roundedRatio.toString();
			contrastEvaluationElem.textContent = getContrastRating(roundedRatio);
		}

		return [fgColor, bgColor];
	}

	function addRange(idx: number, colorInputElem: Element, id: string, label: string, max: string, value: number): void {
		if (!rangeInputTemplate || !inputWrapperElem) return;

		// Clone template
		const clone = document.importNode(rangeInputTemplate, true);

		const labelElem = clone.querySelector<HTMLLabelElement>('label');
		const inputElem = clone.querySelector<HTMLInputElement>('input');

		if (!labelElem || !inputElem) return;

		// Construct range elem
		const className = label.toLowerCase();
		const concatId = `${id}-${className}`;
		labelElem.setAttribute('for', concatId);
		labelElem.insertAdjacentText('afterbegin', label);
		inputElem.id = concatId;
		inputElem.className = className;
		inputElem.max = max;
		inputElem.value = value.toString();

		// Cache elem
		if (rangeInputElems[idx] === undefined) {
			rangeInputElems[idx] = [];
		}
		rangeInputElems[idx].push(inputElem);

		// Add elem
		const clonedNode = clone.firstElementChild;
		const hsvElem = colorInputElem.querySelector('.hsv');
		if (clonedNode && hsvElem) hsvElem.insertAdjacentElement('beforeend', clonedNode);

		// -------------------- onMount --------------------
		// input
		// change
		inputElem.addEventListener('input', () => {
			window.dispatchEvent(
				new CustomEvent('updatehsv', {
					detail: {
						triggeredBy: inputElem,
					},
				}),
			);
		});

		inputElem.addEventListener('change', (event: Event) => {
			const target = event.target as HTMLInputElement | null;
			if (!target) return;

			const { r, g, b } = target.id.includes('foreground') ? color.foreground : color.background;

			pushState(id, rgb2hex(r, g, b));
		});
	}

	function addColorInput(id: string, label: string, value: string): void {
		if (!colorInputTemplate || !inputWrapperElem) return;

		// Clone template
		const clone = document.importNode(colorInputTemplate, true);

		const labelElem = clone.querySelector<HTMLLabelElement>('label');
		const inputElem = clone.querySelector<HTMLInputElement>('input');

		if (!labelElem || !inputElem) return;

		// Construct input elem
		labelElem.setAttribute('for', id);
		labelElem.insertAdjacentText('afterbegin', label);
		inputElem.id = id;
		// inputElem.name = id;
		inputElem.value = getQueryParams(id) || value;

		// Cache elem
		colorInputElems.push(inputElem);

		// Add elem
		const clonedNode = clone.firstElementChild;
		if (clonedNode) {
			const [r, g, b] = hex2rgb(inputElem.value);
			const hsv = rgb2Hsv(r, g, b);

			hsvSettings.forEach((val, i) => {
				addRange(i, clonedNode, id, val.label, val.max, hsv[i]);
			});
			inputWrapperElem.insertAdjacentElement('beforeend', clonedNode);
		}

		// -------------------- onMount --------------------
		// beforeinput
		// input
		// compositionend
		inputElem.addEventListener('beforeinput', (event: InputEvent) => {
			const target = event.target as HTMLInputElement | null;
			if (!target) return;

			const inputChar = event.data;
			const isSelected = target.selectionStart !== target.selectionEnd;

			// Validation (Disallow input)
			if (inputChar && (!hexPattern.test(inputChar) || (target.value.length + inputChar.length > 6 && !isSelected))) {
				event.preventDefault();
			}
		});

		inputElem.addEventListener('input', (event: Event) => {
			const target = event.target as HTMLInputElement | null;
			if (!target) return;

			setHSV(id, target.value);
			pushState(id, target.value);
		});

		inputElem.addEventListener('compositionend', (event: Event) => {
			const target = event.target as HTMLInputElement | null;
			if (!target) return;

			// Remove Hash
			if (target.value.includes('#')) {
				target.value = target.value.replace('#', '');
			}

			// Trim
			if (target.value.length > 6) {
				target.value = target.value.slice(0, 6);
			}

			// Validation (Remove invalid chars)
			if (!hexPattern.test(target.value)) {
				target.value = target.value.replace(/[^0-9a-fA-F]/g, '');
			}

			setHSV(id, target.value);
			pushState(id, target.value);
		});
	}

	function addSampleSentence(): void {
		if (!sampleSentenceElem) return;

		sentence.forEach((str, i) => {
			const div = document.createElement('div');
			div.className = i === 0 ? 'jp' : 'en';

			for (let i = 0; i < 3; i++) {
				const paragraph = document.createElement('p');

				if (i === 1) paragraph.className = 'bold';
				else if (i === 2) paragraph.className = 'large';

				paragraph.insertAdjacentText('beforeend', str);
				div.insertAdjacentElement('beforeend', paragraph);
			}

			sampleSentenceElem.insertAdjacentElement('beforeend', div);
		});
	}

	const init = () => {
		// Add Elems
		addColorInput('foreground', 'Text', defaultFg);
		addColorInput('background', 'Background', defaultBg);
		addSampleSentence();

		setColor();

		// -------------------- onMount --------------------
		// popstate
		// pushstate
		// updatehsv
		window.addEventListener('popstate', () => {
			// console.log('pop');

			const colors = setColor();
			colorInputElems.forEach((colorInputElem, i) => {
				colorInputElem.value = colors[i];
			});
		});

		window.addEventListener('pushstate', () => {
			// console.log('push');

			setColor();
		});

		window.addEventListener('updatehsv', (event: CustomEventInit) => {
			// console.log('update');

			const isForeground = (event.detail.triggeredBy as HTMLInputElement).id.includes('foreground');

			const hsvElems = isForeground ? rangeInputElems.map((elem) => elem[0]) : rangeInputElems.map((elem) => elem[1]);
			const [r, g, b] = hsv2Rgb(hsvElems[0].value, hsvElems[1].value, hsvElems[2].value);

			if (isForeground) {
				color.foreground = { r, g, b };
				colorInputElems[0].value = rgb2hex(r, g, b);

				setColor(colorInputElems[0].value);
			} else {
				color.background = { r, g, b };
				colorInputElems[1].value = rgb2hex(r, g, b);

				setColor(undefined, colorInputElems[1].value);
			}
		});
	};

	init();
});
